/** @format */

import { useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CustomDropDown } from 'src/components/CustomDropDown/CustomDropDown';
import { ArrowDownIcon } from 'src/components/icons';
import { CONTENT_LANGUAGES } from 'src/constants';
import { isExtranetRemovedLocale } from 'src/utils/extranetLanguages';
import { resolveSelectedLanguage, setAppLanguage } from 'src/utils/locale';

const LanguageSection = ({ t, userData, user, onSetData }) => {
  const { i18n } = useTranslation();
  const [languageOpen, setLanguageOpen] = useState(false);

  const languageOptions = useMemo(
    () =>
      [...CONTENT_LANGUAGES]
        .filter(language => !isExtranetRemovedLocale(language.value))
        .sort((a, b) => a.market.localeCompare(b.market)),
    []
  );

  const currentLanguage = useMemo(() => resolveSelectedLanguage(), [i18n.language]);

  const selectedLabel = useMemo(() => {
    const option = languageOptions.find(language => language.value === currentLanguage);
    if (!option) {
      return currentLanguage;
    }

    return `${option.title} - ${option.market}`;
  }, [languageOptions, currentLanguage]);

  const handleLanguageSelect = async value => {
    setLanguageOpen(false);

    if (value === currentLanguage) {
      return;
    }

    onSetData({ target: { name: 'locale', value } });

    const lang = await setAppLanguage(value, { manual: true });
    const url = new URL(window.location.href);
    url.searchParams.set('language', lang);
    window.location.assign(url.toString());
  };

  return (
    <Grid className="personal-data__card personal-data__card--language">
      <h4 className="sub_head">{t('common.language')}</h4>
      <Grid className="personal-data__card-head">
        <div className="personal-data__language-field">
          <label className="personal-data__language-field-label" htmlFor="personal-data-language-trigger">
            {t('common.selectLanguage')}
          </label>
          <CustomDropDown
            className="personal-data__language-dropdown"
            open={languageOpen}
            onOpenChange={setLanguageOpen}
            triggerOpensOnClick={false}
            closeOnPanelClick={false}
            placement="topLeft"
            trigger={
              <button
                id="personal-data-language-trigger"
                className="personal-data__language-trigger"
                type="button"
                aria-label={t('common.selectLanguage')}
                aria-haspopup="menu"
                onClick={() => setLanguageOpen(prev => !prev)}>
                <span className="personal-data__language-trigger-label">{selectedLabel}</span>
                <ArrowDownIcon />
              </button>
            }>
            <ul className="dropdown-selection personal-data__language-menu" role="menu">
              {languageOptions.map(language => {
                const isActive = language.value === currentLanguage;
                return (
                  <li key={language.value} role="none">
                    <button
                      className={`personal-data__language-menu-item${
                        isActive ? ' personal-data__language-menu-item--active' : ''
                      }`}
                      type="button"
                      role="menuitem"
                      onClick={() => handleLanguageSelect(language.value)}>
                      <span className="personal-data__language-menu-item-label">
                        <span className="personal-data__language-menu-item-market">{language.market}</span>
                        <span className="personal-data__language-menu-item-title">{language.title}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </CustomDropDown>
        </div>
      </Grid>
    </Grid>
  );
};

export default LanguageSection;
